import { PATH_LABEL, pathOf, verdictForOffer } from "../content/skill-paths";
import { skillNodeById } from "../content/skill-tree";
import {
  isWeaponModOffer,
  parseWeaponModOffer,
  weaponModById,
  type WeaponModElement,
} from "../content/weapon-mods";
import { hasBanishUnlock, hasRerollUnlock, rerollCharges, type PilotMeta } from "../simulation/pilot-meta";
import type { WorldSnapshot } from "../simulation/snapshot";

const BRANCH_COLOR: Record<string, string> = {
  hull: "#d45c4a",
  shield: "#6ec6ff",
  weapons: "#c45c26",
  mobility: "#8a9a6a",
  utility: "#c9a227",
};

const ELEMENT_COLOR: Record<WeaponModElement, string> = {
  thermal: "#ff6a3a",
  cryo: "#7ec8ff",
  corrosive: "#8bc34a",
  ion: "#b48cff",
  shock: "#ffe066",
};

function offerDisplayName(id: string): string {
  if (isWeaponModOffer(id)) {
    const parsed = parseWeaponModOffer(id);
    const def = parsed ? weaponModById(parsed.modId) : undefined;
    if (def && parsed) return `${def.name} (${parsed.slot})`;
  }
  return skillNodeById(id)?.name ?? id.replace(/_/g, " ");
}

export type SkillPickHandlers = {
  onPickIndex: (index: number) => void;
  onReroll?: () => void;
  onBanish?: (index: number) => void;
};

function coerceOffers(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((entry) => (typeof entry === "string" ? entry.trim() : "")).filter(Boolean);
  }
  return [];
}

function skillPickAllowed(snap: WorldSnapshot): boolean {
  if (snap.paused || snap.runSummary) return false;
  if (snap.phase === "dead" || snap.phase === "extracted" || snap.phase === "carrier") return false;
  return coerceOffers(snap.runProgress.skillPickOffers).length > 0 || snap.runProgress.unspentSkillPoints > 0;
}

function mountNode(node: HTMLElement): void {
  document.body.appendChild(node);
}

/** Level-up overlay — static buttons with direct touch handlers (iOS-safe). */
export class SkillPickPanel {
  private readonly root: HTMLElement;
  private readonly hint: HTMLElement;
  private readonly choiceButtons: HTMLButtonElement[];
  private readonly skipButton: HTMLButtonElement;
  private readonly cardsHost: HTMLElement;
  private readonly actionsHost: HTMLElement;
  private currentOffers: string[] = [];

  public constructor(
    _host: HTMLElement,
    private readonly handlers: SkillPickHandlers,
  ) {
    this.root = document.createElement("aside");
    this.root.className = "skill-pick";
    this.root.hidden = true;
    this.root.innerHTML = `
      <div class="skill-pick-panel" role="dialog" aria-labelledby="skill-pick-title">
        <header class="skill-pick-header">
          <strong id="skill-pick-title">Level Up — Choose Upgrade</strong>
          <p class="skill-pick-hint" data-hint>Tap a button to continue</p>
          <div class="skill-pick-choices">
            <button type="button" class="skill-install-btn" data-choice="0" hidden>1. Upgrade</button>
            <button type="button" class="skill-install-btn" data-choice="1" hidden>2. Upgrade</button>
            <button type="button" class="skill-install-btn" data-choice="2" hidden>3. Upgrade</button>
          </div>
          <button type="button" class="skill-pick-skip" data-skip>Continue (install first upgrade)</button>
        </header>
        <div class="skill-actions" data-actions hidden></div>
        <div class="skill-cards" data-cards></div>
      </div>
    `;
    this.hint = this.root.querySelector("[data-hint]") as HTMLElement;
    this.choiceButtons = [0, 1, 2].map(
      (index) => this.root.querySelector(`[data-choice="${index}"]`) as HTMLButtonElement,
    );
    this.skipButton = this.root.querySelector("[data-skip]") as HTMLButtonElement;
    this.cardsHost = this.root.querySelector("[data-cards]") as HTMLElement;
    this.actionsHost = this.root.querySelector("[data-actions]") as HTMLElement;
    this.wireButtons();
    mountNode(this.root);
  }

  public sync(snap: WorldSnapshot, pilot: PilotMeta): void {
    const offers = coerceOffers(snap.runProgress.skillPickOffers);
    this.currentOffers = offers;
    const show = skillPickAllowed(snap);
    this.root.hidden = !show;
    if (!show) {
      this.hideChoices();
      this.cardsHost.replaceChildren();
      return;
    }

    const key = `${offers.join("|")}:${pilot.rank}:${snap.runProgress.rerollsUsed}:${snap.runProgress.unspentSkillPoints}`;
    if (this.root.dataset.key !== key) {
      this.root.dataset.key = key;
      this.renderActions(pilot, snap.runProgress.rerollsUsed);
      this.renderChoices(offers);
      this.renderCards(offers, snap.runProgress.takenNodes, hasBanishUnlock(pilot));
    }

    this.hint.textContent =
      offers.length > 0
        ? `${offers.length} upgrade${offers.length === 1 ? "" : "s"} available — tap to install`
        : "Tap Continue to install your first upgrade";
  }

  public forceHide(): void {
    this.root.hidden = true;
    this.root.dataset.key = "";
    this.hideChoices();
    this.cardsHost.replaceChildren();
  }

  public dispose(): void {
    this.root.remove();
  }

  private wireButtons(): void {
    let lastFire = 0;
    const activate = (index: number): void => {
      const now = Date.now();
      if (now - lastFire < 350) return;
      lastFire = now;
      if (this.currentOffers.length > 0) {
        const pickIndex = Math.max(0, Math.min(index, this.currentOffers.length - 1));
        this.handlers.onPickIndex(pickIndex);
        return;
      }
      this.handlers.onPickIndex(0);
    };

    const handleTarget = (target: EventTarget | null): void => {
      if (!(target instanceof Element)) return;
      if (target.closest("[data-reroll]")) {
        this.handlers.onReroll?.();
        return;
      }
      const banish = target.closest("[data-banish]") as HTMLElement | null;
      if (banish?.dataset.banish != null) {
        this.handlers.onBanish?.(Number(banish.dataset.banish));
        return;
      }
      if (target.closest("[data-skip]")) {
        activate(0);
        return;
      }
      const choice = target.closest("[data-choice]") as HTMLElement | null;
      if (choice?.dataset.choice != null) {
        activate(Number(choice.dataset.choice));
      }
    };

    this.root.addEventListener(
      "touchend",
      (event) => {
        handleTarget(event.target);
        event.preventDefault();
      },
      { passive: false },
    );
    this.root.addEventListener("click", (event) => {
      handleTarget(event.target);
      event.preventDefault();
    });
  }

  private hideChoices(): void {
    for (const button of this.choiceButtons) {
      button.hidden = true;
    }
  }

  private renderChoices(offers: string[]): void {
    this.currentOffers = offers;
    this.choiceButtons.forEach((button, index) => {
      const id = offers[index];
      if (!id) {
        button.hidden = true;
        return;
      }
      const name = offerDisplayName(id);
      button.hidden = false;
      button.textContent = `${index + 1}. Install ${name}`;
    });
    this.skipButton.textContent =
      offers.length > 0
        ? `Continue (install ${offerDisplayName(offers[0]!)})`
        : "Continue";
  }
  private renderActions(pilot: PilotMeta, rerollsUsed: number): void {
    const canReroll = hasRerollUnlock(pilot) && rerollsUsed < rerollCharges(pilot);
    this.actionsHost.replaceChildren();
    if (!canReroll) {
      this.actionsHost.hidden = true;
      return;
    }
    const reroll = document.createElement("button");
    reroll.type = "button";
    reroll.className = "skill-action";
    reroll.dataset.reroll = "1";
    reroll.textContent = "Reroll offers";
    this.actionsHost.append(reroll);
    this.actionsHost.hidden = false;
  }
  private renderCards(offers: string[], owned: readonly string[], canBanish: boolean): void {
    this.cardsHost.replaceChildren();
    offers.forEach((id, index) => {
      const card = document.createElement("article");
      if (isWeaponModOffer(id)) {
        const parsed = parseWeaponModOffer(id);
        const def = parsed ? weaponModById(parsed.modId) : undefined;
        if (!def || !parsed) return;
        const color = def.element ? ELEMENT_COLOR[def.element] : "#c45c26";
        card.className = "skill-card weapon-mod-card";
        card.style.setProperty("--branch", color);
        card.innerHTML = `
          <span class="skill-key">${index + 1}</span>
          <strong>${def.name}</strong>
          <em>WEAPON MOD · ${parsed.slot.toUpperCase()} · ${def.kind.replace("_", " ").toUpperCase()}</em>
          <p>${def.description}</p>
        `;
      } else {
        const node = skillNodeById(id);
        if (!node) return;
        const color = BRANCH_COLOR[node.branch] ?? "#c45c26";
        const path = pathOf(id);
        const verdict = verdictForOffer(owned, id);
        card.className = `skill-card${node.kind === "keystone" ? " keystone" : ""}`;
        card.dataset.verdict = verdict;
        card.style.setProperty("--branch", color);
        const pathLine = path ? `${PATH_LABEL[path].toUpperCase()} · ` : "";
        const tag =
          verdict === "synergy"
            ? `<span class="skill-verdict syn">SYNERGY</span>`
            : verdict === "conflict"
              ? `<span class="skill-verdict xfl">CONFLICT</span>`
              : "";
        card.innerHTML = `
          <span class="skill-key">${index + 1}</span>
          ${tag}
          <strong>${node.name}</strong>
          <em>${pathLine}${node.branch.toUpperCase()} · T${node.tier}</em>
          <p>${node.description}</p>
        `;
      }
      if (canBanish) {
        const banish = document.createElement("button");
        banish.type = "button";
        banish.className = "skill-banish-btn";
        banish.dataset.banish = String(index);
        banish.textContent = "Banish this run";
        card.append(banish);
      }
      this.cardsHost.append(card);
    });
  }
}

export { skillPickAllowed, coerceOffers };
